import { faLock } from "@fortawesome/pro-solid-svg-icons/faLock";
import { faPhone } from "@fortawesome/pro-solid-svg-icons/faPhone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Badge,
  Button,
  Flex,
  Image,
  Modal,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import Link from "next/link";
import { useCallback, useState } from "react";

import { userService } from "@/app/api/user";
import { useUserContext } from "@/context/user.context";
import { useTrimmedInput } from "@/hooks/use-trimmed-input";
import { MFAMethod, ROLE_DESCRIPTIONS } from "@/types/user";
import { isEmptyAfterTrim } from "@/utils/string";
import ChangePassword from "./change-password";

interface AccountModalProps {
  onClose: () => void;
  opened: boolean;
}

export const AccountModal = ({ onClose, opened }: AccountModalProps) => {
  const { company, refreshUser, user } = useUserContext();
  const nameInput = useTrimmedInput({ initialValue: user?.fullName ?? "" });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handleClose = useCallback(() => {
    setIsChangingPassword(false);
    onClose();
    nameInput.setValue(user?.fullName ?? "");
  }, [nameInput, onClose, user]);

  const onSave = useCallback(() => {
    if (user) {
      userService.updateUserName(nameInput.getTrimmedValue()).finally(() => {
        refreshUser();
        // Intentionally using onClose rather than the wrapped handleClose to
        // keep state in proper sync
        onClose();
      });
    }
  }, [nameInput, onClose, refreshUser, user]);

  return (
    <Modal
      centered
      keepMounted={false}
      onClose={handleClose}
      opened={opened}
      styles={{
        body: {
          padding: "18px 24px 24px 24px",
        },
        content: {
          flex: "none",
        },
      }}
      withCloseButton={false}
    >
      {isChangingPassword ? (
        <ChangePassword onClose={handleClose} />
      ) : (
        <Flex align="flex-start" direction="column">
          <Flex align="center" className="w-full" justify="space-between">
            <Text fw={600}>Your account</Text>
            <Flex align="center">
              {user && (
                <Tooltip label={ROLE_DESCRIPTIONS[user.role]} multiline w={220}>
                  <Badge
                    className="min-w-20 h-6 px-2 py-0.5 uppercase font-bold text-xs tracking-wider"
                    color="mint-green.2"
                    c="mint-green.10"
                    variant="filled"
                    radius="xl"
                  >
                    {user.role}
                  </Badge>
                </Tooltip>
              )}
              <ActionIcon onClick={handleClose} variant="transparent">
                <Image
                  src="/assets/circle-x.svg"
                  alt="Close Account Modal"
                  h={16}
                />
              </ActionIcon>
            </Flex>
          </Flex>
          <TextInput
            className="w-[300px]"
            disabled
            label="Email"
            type="email"
            value={user?.email}
          />
          <TextInput
            className="w-[300px]"
            label="Full name"
            my={12}
            onBlur={nameInput.onBlur}
            onChange={nameInput.onChange}
            value={nameInput.value}
          />
          {company?.mfaMethods?.includes(MFAMethod.SMS) && (
            <Button
              className="uppercase"
              component={Link}
              href="/auth/change-phone"
              rightSection={<FontAwesomeIcon icon={faPhone} />}
              p={0}
              variant="transparent"
            >
              Change Phone Number
            </Button>
          )}
          <Button
            className="uppercase"
            onClick={() => setIsChangingPassword(true)}
            p={0}
            rightSection={<FontAwesomeIcon icon={faLock} />}
            variant="transparent"
          >
            Change Password
          </Button>
          <Flex mt={21}>
            <Button
              className="disabled:bg-uw-green disabled:opacity-50 disabled:text-white"
              color="mint-green.8"
              disabled={
                isEmptyAfterTrim(nameInput.value) ||
                nameInput.getTrimmedValue() === user?.fullName
              }
              mr={9}
              onClick={onSave}
            >
              Save changes
            </Button>
            <Button
              className="text-uw-gray-9"
              color="gray.0"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Flex>
        </Flex>
      )}
    </Modal>
  );
};
